import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { deployments, inventory } from "@/api/endpoints";
import { Spinner } from "@/components/shared/Spinner";
import { useToast } from "@/components/shared/Toast";

interface Props {
  tempoId: string;
  deploymentName: string;
  /** Already-assigned device IDs so we hide them (or show as "already here"). */
  excludeDeviceIds: number[];
  onClose: () => void;
}

export function AddDevicesDialog({
  tempoId,
  deploymentName,
  excludeDeviceIds,
  onClose,
}: Props) {
  const qc = useQueryClient();
  const showToast = useToast((s) => s.show);
  const [picked, setPicked] = useState<Set<number>>(new Set());
  const [filter, setFilter] = useState("");

  const inv = useQuery({
    queryKey: ["inventory", "for-add-to-deployment"],
    queryFn: () => inventory.list({}),
    staleTime: 60_000,
  });

  const candidates = useMemo(() => {
    const rows = inv.data ?? [];
    const excluded = new Set(excludeDeviceIds);
    const filtered = rows.filter((r) => !excluded.has(r.netbox_id));
    const f = filter.trim().toLowerCase();
    if (!f) return filtered;
    return filtered.filter(
      (r) =>
        r.name.toLowerCase().includes(f) ||
        (r.gpu_type ?? "").toLowerCase().includes(f) ||
        (r.tenant ?? "").toLowerCase().includes(f) ||
        (r.tempo_deployment_xref ?? "").toLowerCase().includes(f),
    );
  }, [inv.data, excludeDeviceIds, filter]);

  const assign = useMutation({
    mutationFn: () => deployments.assign(tempoId, Array.from(picked)),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["revenue", "deployment", tempoId] });
      qc.invalidateQueries({ queryKey: ["inventory"] });
      if (res.failed.length === 0) {
        showToast(`Added ${res.succeeded.length} device(s).`, "success");
        onClose();
      } else {
        showToast(
          `Added ${res.succeeded.length}; ${res.failed.length} failed. Check console for details.`,
          "error",
        );
        console.warn("AddDevices failures:", res.failed);
      }
    },
    onError: (err) => showToast(err instanceof Error ? err.message : "add failed", "error"),
  });

  const toggle = (id: number) =>
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const selectAll = () => setPicked(new Set(candidates.map((r) => r.netbox_id)));
  const clear = () => setPicked(new Set());

  return (
    <div className="fixed inset-0 z-40 grid place-items-center bg-black/40 px-4">
      <div className="w-full max-w-2xl rounded-xl bg-white p-5 shadow-xl dark:bg-slate-900">
        <h2 className="text-base font-semibold mb-1">
          Add devices to {deploymentName}
        </h2>
        <p className="text-xs text-slate-500 mb-3">
          Devices already assigned to this deployment are hidden. Selecting a device that's
          currently assigned to a *different* deployment will move it to this one.
        </p>

        <div className="flex items-center gap-3 mb-3">
          <input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter by hostname, GPU, tenant…"
            className="flex-1 rounded-md border border-slate-300 px-3 py-2 text-sm dark:bg-slate-800 dark:border-slate-700"
          />
          <div className="text-xs text-slate-500">
            {picked.size} of {candidates.length} selected
          </div>
          <button
            onClick={selectAll}
            disabled={candidates.length === 0}
            className="text-xs text-brand-700 hover:underline disabled:opacity-50"
          >
            Select all
          </button>
          {picked.size > 0 && (
            <button onClick={clear} className="text-xs text-slate-500 hover:text-slate-900">
              Clear
            </button>
          )}
        </div>

        <div className="max-h-80 overflow-y-auto rounded-md border border-slate-200 dark:border-slate-800">
          {inv.isLoading ? (
            <div className="p-4"><Spinner label="Loading inventory…" /></div>
          ) : candidates.length === 0 ? (
            <div className="p-4 text-sm text-slate-500">
              {filter ? "No devices match the filter." : "No devices available to add."}
            </div>
          ) : (
            <table className="min-w-full text-sm">
              <tbody>
                {candidates.map((r) => (
                  <tr
                    key={r.netbox_id}
                    onClick={() => toggle(r.netbox_id)}
                    className="cursor-pointer border-t border-slate-100 hover:bg-slate-50/60 dark:border-slate-800 dark:hover:bg-slate-800/30"
                  >
                    <td className="px-3 py-2 w-8">
                      <input
                        type="checkbox"
                        checked={picked.has(r.netbox_id)}
                        readOnly
                      />
                    </td>
                    <td className="px-3 py-2 font-medium">{r.name}</td>
                    <td className="px-3 py-2 text-slate-600 dark:text-slate-300">
                      {r.gpu_type ?? "—"}
                    </td>
                    <td className="px-3 py-2 text-slate-600 dark:text-slate-300">
                      {r.tenant ?? "—"}
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-500">
                      {r.tempo_deployment_xref ? (
                        <span title={r.tempo_deployment_xref}>
                          in another deployment
                        </span>
                      ) : (
                        "unassigned"
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="mt-4 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-3 py-2 text-sm rounded-md hover:bg-slate-100 dark:hover:bg-slate-800"
          >
            Cancel
          </button>
          <button
            onClick={() => assign.mutate()}
            disabled={picked.size === 0 || assign.isPending}
            className="px-3 py-2 text-sm rounded-md bg-brand-500 text-white hover:bg-brand-700 disabled:opacity-50"
          >
            {assign.isPending ? "Adding…" : `Add ${picked.size || ""}`}
          </button>
        </div>
      </div>
    </div>
  );
}
